import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrandMark } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function Navbar({ minimal = false }) {
  const { user, logout } = useAuth();
  const cart = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  const NAV_ITEMS = [
    { to: "/locations", label: "Locations", testId: "nav-locations-link" },
    { to: "/how-it-works", label: "How It Works", testId: "nav-how-it-works-link" },
    { to: "/inventory", label: "Inventory", testId: "nav-inventory-link" },
  ];

  return (
    <>
      {/* Top strip */}
      <div className="bg-brand-blue text-white py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center sm:justify-end text-[10px] font-black uppercase tracking-[0.3em]">
          <span className="text-white/70">
            Denver&apos;s first self-serve rental pod network
          </span>
        </div>
      </div>

      <nav className="bg-white shadow-sm sticky top-0 z-40 border-b-4 border-brand-blue">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <BrandMark />

          {!minimal && (
            <div className="hidden lg:flex items-center gap-8">
              <div className="flex gap-6 font-black text-brand-blue uppercase text-xs tracking-widest">
                {NAV_ITEMS.map((it) => (
                  <Link key={it.to} to={it.to} className="hover:text-brand-red transition-colors" data-testid={it.testId}>
                    {it.label}
                  </Link>
                ))}
                {user?.role === "admin" && (
                  <Link to="/admin" className="text-brand-red" data-testid="nav-admin-link">
                    Command
                  </Link>
                )}
              </div>

              <div className="h-6 w-px bg-slate-200" />

              {user ? (
                <div className="flex items-center gap-3">
                  <Link
                    to="/dashboard"
                    className="text-xs font-black uppercase text-brand-blue hover:text-brand-red"
                    data-testid="nav-dashboard-link"
                  >
                    {user.name.split(" ")[0]}
                  </Link>
                  <Link
                    to="/profile"
                    className="text-xs font-black uppercase text-brand-blue hover:text-brand-red flex items-center gap-1.5"
                    data-testid="nav-profile-link"
                  >
                    {user.id_verified ? (
                      <i className="fas fa-circle-check text-emerald-500" />
                    ) : (
                      <i className="fas fa-circle-exclamation text-brand-red animate-pulse" />
                    )}
                    Profile
                  </Link>
                  <button
                    onClick={() => { logout(); navigate("/"); }}
                    className="bg-slate-100 text-brand-blue px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border border-slate-200 hover:bg-brand-red hover:text-white hover:border-brand-red transition-colors"
                    data-testid="nav-logout-button"
                  >
                    Log Out
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login" className="text-xs font-black uppercase text-brand-blue hover:text-brand-red" data-testid="nav-login-link">Login</Link>
                  <Link to="/register" className="bg-slate-100 text-brand-blue px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border border-slate-200" data-testid="nav-register-link">Sign Up</Link>
                </div>
              )}

              <button
                onClick={() => cart.setOpen(true)}
                className="relative bg-brand-blue text-white px-5 py-2.5 rounded-full font-black flex items-center shadow-lg hover:shadow-xl transition-shadow"
                data-testid="open-cart-button"
              >
                <i className="fas fa-shopping-basket mr-2 text-brand-gold" />
                <span data-testid="cart-count-badge">{cart.totals.count}</span>
              </button>
            </div>
          )}

          {/* Mobile: hamburger + cart */}
          {!minimal && (
            <div className="lg:hidden flex items-center gap-2">
              <button
                onClick={() => cart.setOpen(true)}
                className="bg-brand-blue text-white px-4 py-2 rounded-full font-black flex items-center"
                data-testid="open-cart-button-mobile"
              >
                <i className="fas fa-shopping-basket mr-2 text-brand-gold" />
                {cart.totals.count}
              </button>
              <button
                onClick={() => setMobileOpen((v) => !v)}
                className="bg-slate-100 text-brand-blue px-3 py-2 rounded-full font-black"
                data-testid="nav-mobile-toggle"
                aria-label="Toggle menu"
              >
                <i className={`fas ${mobileOpen ? "fa-xmark" : "fa-bars"} text-lg`} />
              </button>
            </div>
          )}
        </div>

        {/* Mobile drawer */}
        {!minimal && mobileOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg" data-testid="nav-mobile-drawer">
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              {NAV_ITEMS.map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  onClick={closeMobile}
                  className="block py-3 px-2 font-black uppercase text-xs tracking-widest text-brand-blue hover:text-brand-red border-b border-slate-100"
                  data-testid={`${it.testId}-mobile`}
                >
                  {it.label}
                </Link>
              ))}
              {user?.role === "admin" && (
                <Link to="/admin" onClick={closeMobile} className="block py-3 px-2 font-black uppercase text-xs tracking-widest text-brand-red border-b border-slate-100" data-testid="nav-admin-link-mobile">
                  Command
                </Link>
              )}
              <div className="pt-3 flex gap-2">
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={closeMobile} className="flex-1 text-center py-2.5 rounded-full bg-slate-100 text-brand-blue font-black uppercase text-[10px] tracking-widest" data-testid="nav-dashboard-link-mobile">Dashboard</Link>
                    <Link to="/profile" onClick={closeMobile} className="flex-1 text-center py-2.5 rounded-full bg-slate-100 text-brand-blue font-black uppercase text-[10px] tracking-widest" data-testid="nav-profile-link-mobile">Profile</Link>
                    <button onClick={() => { closeMobile(); logout(); navigate("/"); }} className="flex-1 py-2.5 rounded-full bg-brand-red text-white font-black uppercase text-[10px] tracking-widest" data-testid="nav-logout-button-mobile">Log Out</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={closeMobile} className="flex-1 text-center py-2.5 rounded-full bg-slate-100 text-brand-blue font-black uppercase text-[10px] tracking-widest" data-testid="nav-login-link-mobile">Login</Link>
                    <Link to="/register" onClick={closeMobile} className="flex-1 text-center py-2.5 rounded-full bg-brand-red text-white font-black uppercase text-[10px] tracking-widest" data-testid="nav-register-link-mobile">Sign Up</Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
